import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './Transactions.css';

const Transactions = ({ caseData, onBack, token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  
  // Debug log
  useEffect(() => {
    console.log('Transactions component mounted with caseData:', caseData);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    transaction_purpose: 'Civil Case',
    payment_account: 'Online',
    transaction_title: '',
    amount: '',
    transaction_image: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const purposeOptions = ['Civil Case', 'Criminal Case', 'Family Case', 'Corporate Case', 'Other'];
  const paymentOptions = ['Online', 'Cash', 'Bank Transfer', 'Cheque'];

  // Fetch transactions for this case
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Use case_id from caseData if available, otherwise use 0
      const caseId = caseData && caseData.id ? caseData.id : 0;
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/get/transaction/${caseId}`;
      console.log('Fetching transactions from:', apiUrl);
      console.log('Case ID:', caseId);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Transactions response:', data);

      if (response.ok && data.data && Array.isArray(data.data)) {
        setTransactions(data.data);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, transaction_image: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Create transaction
  const handleCreateTransaction = async (e) => {
    e.preventDefault();

    if (!formData.transaction_title || !formData.amount) {
      Swal.fire({
        title: 'Warning',
        text: 'Please fill in all required fields',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setSubmitting(true);
      
      // Use case_id from caseData if available, otherwise use 0
      const caseId = caseData && caseData.id ? caseData.id : 0;
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/add/transaction`;
      console.log('Creating transaction:', apiUrl);
      console.log('Case ID:', caseId);

      const formDataToSend = new FormData();
      formDataToSend.append('case_id', caseId);
      formDataToSend.append('transaction_purpose', formData.transaction_purpose);
      formDataToSend.append('payment_account', formData.payment_account);
      formDataToSend.append('trancaction_title', formData.transaction_title); // API expects 'trancaction_title'
      formDataToSend.append('amount', formData.amount);
      
      if (formData.transaction_image) {
        formDataToSend.append('transaction_image', formData.transaction_image);
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formDataToSend,
      });

      const data = await response.json();
      console.log('Create transaction response:', data);
      console.log('Response status:', response.status);
      
      // Log all form data being sent for debugging
      console.log('FormData being sent:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Transaction created successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        setShowGenerateModal(false);
        setFormData({
          transaction_purpose: 'Civil Case',
          payment_account: 'Online',
          transaction_title: '',
          amount: '',
          transaction_image: null
        });
        setImagePreview(null);
        fetchTransactions(); // Refresh the list
      } else {
        // Extract validation errors if available
        let errorMessage = data.message || 'Failed to create transaction';
        
        if (data.errors) {
          // Laravel validation errors
          const errorMessages = Object.values(data.errors).flat();
          errorMessage = errorMessages.join('\n');
        }
        
        console.error('Transaction creation failed:', data);
        
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error creating transaction:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to create transaction',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this transaction?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeletingTransaction(transactionId);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/transactions/${transactionId}`;
      console.log('Deleting transaction:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Delete transaction response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Deleted!',
          text: 'Transaction deleted successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        fetchTransactions(); // Refresh the list
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete transaction',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to delete transaction',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeletingTransaction(null);
    }
  };

  // Toggle transaction details
  const toggleTransactionDetails = (transactionId) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  useEffect(() => {
    // Fetch transactions regardless of whether caseData exists or not
    fetchTransactions();
  }, [caseData]);

  return (
    <div className="transactions-container">
      {/* Header */}
      <div className="transactions-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>
          {caseData && caseData.id 
            ? `Transactions - Case #${caseData.caseNumber || caseData.id}` 
            : 'All Transactions'}
        </h2>
      </div>

      {/* Transactions List */}
      <div className="transactions-content">
        {loading ? (
          <div className="loading-state">
            <p>Loading transactions...</p>
          </div>
        ) : transactions.length > 0 ? (
          <div className="transactions-list">
            {transactions.map(transaction => {
              const isExpanded = expandedTransaction === transaction.id;
              const date = new Date(transaction.created_at);
              const formattedDate = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: '2-digit' 
              });

              return (
                <div key={transaction.id} className={`transaction-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="transaction-header-section" onClick={() => toggleTransactionDetails(transaction.id)}>
                    <div className="transaction-icon">
                      <span className="dollar-icon">$</span>
                    </div>
                    
                    <div className="transaction-summary">
                      <div className="transaction-title-row">
                        <h3 className="transaction-name">{transaction.trancaction_title || 'Untitled'}</h3>
                        <button
                          className="delete-transaction-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTransaction(transaction.id);
                          }}
                          disabled={deletingTransaction === transaction.id}
                        >
                          {deletingTransaction === transaction.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                      <p className="transaction-date">{formattedDate}</p>
                    </div>
                    
                    <div className="transaction-amount-section">
                      <p className="transaction-amount">+ Rs.{transaction.amount}</p>
                      <p className="transaction-purpose">{transaction.transaction_purpose}</p>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="transaction-details">
                      <h4>Transaction Details</h4>
                      
                      <div className="detail-row">
                        <span className="detail-label">Transaction Title:</span>
                        <span className="detail-value">{transaction.trancaction_title || 'N/A'}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Payment Account:</span>
                        <span className="detail-value">{transaction.payment_account || 'N/A'}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Amount:</span>
                        <span className="detail-value">Rs. {transaction.amount}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Transaction Purpose:</span>
                        <span className="detail-value">{transaction.transaction_purpose}</span>
                      </div>

                      {transaction.transaction_image && (
                        <div className="transaction-image-section">
                          <span className="detail-label">Attachment:</span>
                          <img 
                            src={`${process.env.REACT_APP_API_URL}/${transaction.transaction_image}`} 
                            alt="Transaction" 
                            className="transaction-image"
                          />
                        </div>
                      )}
                      
                      <button className="collapse-button" onClick={() => setExpandedTransaction(null)}>
                        ↑
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-transactions-state">
            <p>No transactions found for this case.</p>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <button className="fab-button" onClick={() => setShowGenerateModal(true)}>
        <span className="fab-icon">📊</span>
      </button>

      {/* Generate Transaction Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal-content generate-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="back-button" onClick={() => setShowGenerateModal(false)}>
                ←
              </button>
              <h3>Generate Transaction</h3>
            </div>

            <form onSubmit={handleCreateTransaction} className="modal-body">
              {/* Transaction Purpose */}
              <div className="form-group">
                <label>Transaction Purpose</label>
                <select
                  value={formData.transaction_purpose}
                  onChange={(e) => setFormData({ ...formData, transaction_purpose: e.target.value })}
                  className="form-select"
                >
                  {purposeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Payment Account */}
              <div className="form-group">
                <label>Select Payment Account</label>
                <select
                  value={formData.payment_account}
                  onChange={(e) => setFormData({ ...formData, payment_account: e.target.value })}
                  className="form-select"
                >
                  {paymentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Transaction Title */}
              <div className="form-group">
                <label>Transaction Title *</label>
                <input
                  type="text"
                  placeholder="Transaction Title"
                  value={formData.transaction_title}
                  onChange={(e) => setFormData({ ...formData, transaction_title: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              {/* Amount */}
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  placeholder="Amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="form-input"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <label htmlFor="transaction-image" className="image-upload-area">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <>
                      <span className="camera-icon">📷</span>
                      <p>Upload Transaction image or document</p>
                    </>
                  )}
                </label>
                <input
                  id="transaction-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                  style={{ display: 'none' }}
                />
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="submit-button generate-button"
                disabled={submitting}
              >
                {submitting ? 'Generating...' : 'Generate'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
